import React, { useCallback, useEffect, useRef } from 'react';
import OrbBall from '../../../components/OrbBall/OrbBall';

const DOTS = 7;
const TAPS_PER_DOT = 2;
const TAPS_TO_CHARGE = DOTS * TAPS_PER_DOT;
const IDLE_REVEAL_MS = 4000; // a player who stops tapping still gets an answer
const MIN_TAPS_FOR_IDLE_REVEAL = 3;

const INTENSITY_LABELS = ['Gathering energy…', 'The orb stirs…', 'The orb is listening…', 'The orb is glowing…', 'The orb can feel it…'];

export default function Charging({ mood, tapCount, intensity, onTap, onCharged }) {
  const chargedRef = useRef(false);

  // Both timers below restart on every tap, so the guard lives here rather
  // than in the effects — otherwise a tap landing inside the reveal delay
  // would cancel the pending timer and never schedule another.
  const revealOnce = useCallback(() => {
    if (chargedRef.current) return;
    chargedRef.current = true;
    onCharged();
  }, [onCharged]);

  // Auto-reveal a beat after the orb is fully charged.
  useEffect(() => {
    if (tapCount < TAPS_TO_CHARGE) return;
    const timer = setTimeout(revealOnce, 420);
    return () => clearTimeout(timer);
  }, [tapCount, revealOnce]);

  // Reveal anyway if they stop tapping — a calm tapper still deserves a prophecy.
  useEffect(() => {
    if (tapCount < MIN_TAPS_FOR_IDLE_REVEAL) return;
    const timer = setTimeout(revealOnce, IDLE_REVEAL_MS);
    return () => clearTimeout(timer);
  }, [tapCount, revealOnce]);

  const filledDots = Math.min(DOTS, Math.floor(tapCount / TAPS_PER_DOT));

  return (
    <div className="orb-screen">
      <div className="orb-charge-hint">✦ Tap the Orb ✦</div>
      <p className="orb-sub">to reveal your answer</p>

      <div className="orb-spacer" />
      <OrbBall
        mood={mood}
        size={230}
        intensity={intensity}
        tappable
        shaking={tapCount > 0}
        expression={tapCount === 0 ? 'idle' : intensity >= 2 ? 'excited' : 'curious'}
        onTap={onTap}
        ariaLabel="Tap the orb to charge it"
      />
      <div className="orb-spacer" />

      <div className="orb-desperation">
        {tapCount === 0 ? 'Keep tapping…' : INTENSITY_LABELS[intensity]}
      </div>

      <div className="orb-progress-dots">
        {Array.from({ length: DOTS }, (_, i) => (
          <span key={i} className={`orb-dot${i < filledDots ? ' is-on' : ''}`} />
        ))}
      </div>
    </div>
  );
}
